import { useState, FormEvent, KeyboardEvent, useEffect, useRef } from 'react'
import { OSM_NOMINATIM_URL, OSM_USER_AGENT } from '../config/env'

import { SEARCH_ICON, VIEW_ZONES_ICON } from '../assets/images'

interface TopbarProps {
  readonly onViewZones: () => void
  readonly onSearch: (
    lat: number,
    lng: number,
    zoom: number,
    displayName?: string
  ) => void
  readonly onMobileMenuToggle?: () => void
  readonly isMapInfoOpen?: boolean
  readonly onMapInfoClose?: () => void
}

interface Coordinates {
  lat: number
  lng: number
}

interface GeocodeResult extends Coordinates {
  displayName: string
}

export default function Topbar({
  onViewZones,
  onSearch,
  onMobileMenuToggle,
  isMapInfoOpen = false,
  onMapInfoClose,
}: TopbarProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [validationError, setValidationError] = useState('')
  const [suggestions, setSuggestions] = useState<GeocodeResult[]>([])
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false)
  const suppressSuggestionsRef = useRef(false)

  // Handle input change with validation
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    // Allow empty input
    if (value === '') {
      setSearchQuery('')
      setValidationError('')
      return
    }

    // Filter out invalid characters - only keep valid ones
    const filteredValue = value
      .split('')
      .filter(char => {
        // Allow: letters, numbers, spaces, commas, periods, plus/minus, semicolons, apostrophes, quotes
        return /[a-zA-Z0-9\s,.\-+;'"]/.test(char)
      })
      .join('')

    // Only update if the filtered value is different (invalid characters were removed)
    if (filteredValue !== value) {
      setValidationError(
        'Invalid characters removed. Only letters, numbers, spaces, commas, periods, apostrophes, and +/- signs are allowed'
      )
      // Clear error after 3 seconds
      setTimeout(() => setValidationError(''), 3000)
    } else {
      setValidationError('')
    }

    setSearchQuery(filteredValue)
  }

  // Parse coordinates (supports lat,lng or lng,lat formats)
  const parseCoordinates = (query: string): Coordinates | null => {
    const trimmed = query.trim()
    if (trimmed.length > 50) return null

    // Try to match coordinate patterns (lat,lng or lat;lng)
    // Using a more specific pattern to avoid ReDoS:
    // [+-]?\d+(?:\.\d+)? matches numbers like 1, 1.2, +1, -1.2
    // (?:\.\d+)? matches .2 (optional dot followed by digits)
    const num = /[+-]?(?:\d+(?:\.\d+)?|\.\d+)/.source
    const coordPattern = new RegExp(`^(${num})\\s*[,;]\\s*(${num})$`)
    const match = coordPattern.exec(trimmed)

    if (match) {
      const lat = parseFloat(match[1] ?? '0')
      const lng = parseFloat(match[2] ?? '0')

      // Validate coordinate ranges
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return { lat, lng }
      }
    }
    return null
  }

  // Geocode address using OpenStreetMap Nominatim
  const geocodeAddress = async (
    address: string
  ): Promise<GeocodeResult | null> => {
    try {
      const response = await fetch(
        `${OSM_NOMINATIM_URL}/search?format=json&q=${encodeURIComponent(
          address
        )}&limit=1`,
        {
          headers: {
            'User-Agent': OSM_USER_AGENT,
          },
        }
      )
      const data = (await response.json()) as Array<{
        lat: string
        lon: string
        display_name: string
      }>

      if (data && data.length > 0 && data[0]) {
        return {
          lat: parseFloat(data[0].lat || '0'),
          lng: parseFloat(data[0].lon || '0'),
          displayName: data[0].display_name || '',
        }
      }
      return null
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Geocoding error:', error)
      return null
    }
  }

  /**
   * Fetch address suggestions for the autocomplete dropdown
   */
  const fetchSuggestions = async (query: string): Promise<void> => {
    const trimmed = query.trim()
    if (!trimmed) {
      setSuggestions([])
      return
    }

    try {
      setIsFetchingSuggestions(true)

      const response = await fetch(
        `${OSM_NOMINATIM_URL}/search?format=json&q=${encodeURIComponent(
          trimmed
        )}&limit=5`,
        {
          headers: {
            'User-Agent': OSM_USER_AGENT,
          },
        }
      )

      const data = (await response.json()) as Array<{
        lat: string
        lon: string
        display_name: string
      }>

      if (Array.isArray(data) && data.length > 0) {
        const mapped: GeocodeResult[] = data.map(item => ({
          lat: parseFloat(item.lat || '0'),
          lng: parseFloat(item.lon || '0'),
          displayName: item.display_name || '',
        }))
        setSuggestions(mapped)
      } else {
        setSuggestions([])
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Suggestion fetch error:', error)
      setSuggestions([])
    } finally {
      setIsFetchingSuggestions(false)
    }
  }

  /**
   * Debounce suggestions while typing
   */
  useEffect(() => {
    // Skip one effect run when we explicitly suppress suggestions
    if (suppressSuggestionsRef.current) {
      suppressSuggestionsRef.current = false
      return
    }

    if (!searchQuery || searchQuery.trim().length < 3) {
      setSuggestions([])
      setIsFetchingSuggestions(false)
      return
    }

    const timeoutId = setTimeout(() => {
      void fetchSuggestions(searchQuery)
    }, 400)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [searchQuery])

  const handleSuggestionClick = (suggestion: GeocodeResult) => {
    // Prevent debounce effect from immediately refetching suggestions
    suppressSuggestionsRef.current = true
    setSearchQuery(suggestion.displayName)
    setSuggestions([])
    setIsFetchingSuggestions(false)
    onSearch(suggestion.lat, suggestion.lng, 12, suggestion.displayName)
  }

  const handleSearch = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!searchQuery.trim() || !onSearch) return

    setIsSearching(true)

    try {
      // First, try to parse as coordinates
      const coords = parseCoordinates(searchQuery)

      if (coords) {
        // Direct coordinate search
        onSearch(coords.lat, coords.lng, 12)
      } else {
        // Geocode address
        const result = await geocodeAddress(searchQuery)

        if (result) {
          onSearch(result.lat, result.lng, 12, result.displayName)
        } else {
          // eslint-disable-next-line no-console
          console.warn(
            'Location not found. Please try a different address or coordinates.'
          )
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Search error:', error)
      // eslint-disable-next-line no-console
      console.error('An error occurred while searching. Please try again.')
    } finally {
      setIsSearching(false)
    }
  }

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearch(e as unknown as FormEvent<HTMLFormElement>)
    }
  }

  return (
    <>
      <div className='block sm:hidden absolute top-4 left-4 right-4 z-[900] pointer-events-none'>
        <div
          className={`pointer-events-auto bg-white border border-[#e0e0e0] rounded-full shadow-md flex items-center gap-2 px-3 py-2 transition-all duration-300 relative ${
            isMapInfoOpen ? 'backdrop-blur-sm opacity-70' : ''
          }`}
        >
          {/* Transparent Overlay Button to close MapInfo when it's open */}
          {isMapInfoOpen && onMapInfoClose && (
            <button
              type='button'
              className='absolute inset-0 w-full h-full bg-transparent border-none p-0 cursor-pointer rounded-full z-10'
              onClick={onMapInfoClose}
              aria-label='Close map information'
            />
          )}

          {/* Hamburger Menu Button - inside the bar */}
          <button
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
              if (onMobileMenuToggle) {
                onMobileMenuToggle()
              }
            }}
            className='flex-shrink-0 text-gray-700 relative z-20'
            aria-label='Toggle menu'
            type='button'
          >
            <svg
              className='w-5 h-5'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M4 6h16M4 12h16M4 18h16'
              />
            </svg>
          </button>

          {/* Search Input */}
          <form
            onSubmit={handleSearch}
            className='flex-1 relative z-20'
            onClick={e => {
              if (isMapInfoOpen && onMapInfoClose) {
                e.stopPropagation()
                onMapInfoClose()
              }
            }}
            onKeyDown={e => {
              if (e.key === 'Escape' && isMapInfoOpen && onMapInfoClose) {
                onMapInfoClose()
              }
            }}
          >
            <input
              type='text'
              value={searchQuery}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder='Search address or coordinates...'
              className={`w-full bg-transparent border-0 focus:outline-none text-sm placeholder:text-gray-500 ${
                validationError ? 'text-red-600' : 'text-gray-800'
              }`}
              disabled={isSearching}
            />
            {/* Mobile suggestions / loading */}
            {(isFetchingSuggestions || suggestions.length > 0) && (
              <div className='absolute left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-64 overflow-auto'>
                {isFetchingSuggestions && suggestions.length === 0 ? (
                  <div className='flex items-center gap-2 px-3 py-2 text-xs text-gray-500'>
                    <span className='inline-block h-3 w-3 border-2 border-purple-400 border-t-transparent rounded-full animate-spin' />
                    <span>Searching…</span>
                  </div>
                ) : (
                  suggestions.map(suggestion => (
                    <button
                      key={`${suggestion.lat}-${suggestion.lng}-${suggestion.displayName}`}
                      type='button'
                      onClick={e => {
                        e.preventDefault()
                        handleSuggestionClick(suggestion)
                      }}
                      className='w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 border-b last:border-b-0 cursor-pointer'
                    >
                      {suggestion.displayName}
                    </button>
                  ))
                )}
              </div>
            )}
            {validationError && (
              <div className='absolute top-full left-0 mt-1 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1 z-10'>
                {validationError}
              </div>
            )}
            {isSearching && (
              <div className='absolute right-0 top-1/2 -translate-y-1/2 font-normal'>
                <div className='animate-spin rounded-full h-3 w-3 border-2 border-purple-500 border-t-transparent'></div>
              </div>
            )}
          </form>

          {/* View Zones Button - icon only */}
          <button
            onClick={onViewZones}
            className='flex-shrink-0 relative z-20'
            aria-label='View Zones'
            type='button'
          >
            <img src={VIEW_ZONES_ICON} alt='View Zones' className='w-5 h-5' />
          </button>
        </div>
      </div>

      {/* Desktop Search Bar */}
      <div className='hidden sm:block absolute top-4 right-4 z-[900] pointer-events-none'>
        <div className='flex flex-row items-center gap-3 sm:gap-4'>
          {/* Search */}
          <div
            className={`w-auto min-w-[200px] sm:min-w-[300px] max-w-[400px] pointer-events-auto transition-all duration-300 ${
              isMapInfoOpen
                ? 'backdrop-blur-sm opacity-70 sm:backdrop-blur-none sm:opacity-100'
                : ''
            }`}
          >
            <form
              onSubmit={handleSearch}
              className='relative'
              onClick={e => {
                if (isMapInfoOpen && onMapInfoClose) {
                  e.stopPropagation()
                  onMapInfoClose()
                }
              }}
              onKeyDown={e => {
                if (e.key === 'Escape' && isMapInfoOpen && onMapInfoClose) {
                  onMapInfoClose()
                }
              }}
            >
              <input
                type='text'
                value={searchQuery}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder='Search address or coordinates...'
                className={`w-full bg-white/95 backdrop-blur border rounded-xl shadow pl-10 sm:pl-12 pr-5 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                  validationError
                    ? 'border-red-300 focus:ring-red-300'
                    : 'border-gray-200 focus:ring-purple-300'
                }`}
                disabled={isSearching}
              />
              {/* Desktop suggestions / loading */}
              {(isFetchingSuggestions || suggestions.length > 0) && (
                <div className='absolute left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-64 overflow-auto'>
                  {isFetchingSuggestions && suggestions.length === 0 ? (
                    <div className='flex items-center gap-2 px-3 py-2 text-sm text-gray-500'>
                      <span className='inline-block h-4 w-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin' />
                      <span>Searching…</span>
                    </div>
                  ) : (
                    suggestions.map(suggestion => (
                      <button
                        key={`${suggestion.lat}-${suggestion.lng}-${suggestion.displayName}`}
                        type='button'
                        onClick={e => {
                          e.preventDefault()
                          handleSuggestionClick(suggestion)
                        }}
                        className='w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 border-b last:border-b-0'
                      >
                        {suggestion.displayName}
                      </button>
                    ))
                  )}
                </div>
              )}
              {validationError && (
                <div className='absolute top-full left-0 mt-1 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1 z-10'>
                  {validationError}
                </div>
              )}
              <img
                src={SEARCH_ICON}
                alt='Search'
                className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5'
              />
              {isSearching && (
                <div className='absolute right-3 top-1/2 -translate-y-1/2'>
                  <div className='animate-spin rounded-full h-4 w-4 border-2 border-purple-500 border-t-transparent'></div>
                </div>
              )}
            </form>
          </div>
          {/* View Zones Button */}
          <div className='pointer-events-auto'>
            <button
              onClick={onViewZones}
              className='w-auto bg-white hover:bg-gray-50 text-[#7B61FF] font-semibold px-4 py-2.5 rounded-xl shadow border border-gray-200 transition-colors flex items-center justify-center gap-2'
            >
              <img src={VIEW_ZONES_ICON} alt='View Zones' className='w-5 h-5' />
              <span className='hidden sm:inline'>View Zones</span>
              <span className='sm:hidden'>Zones</span>
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
